import { useState, type ReactNode } from 'react';
import { MAX_TEXT_LENGTH, validateTitle } from '../../core/profile';
import { initials } from '../format';
import { EntryForm } from './EntryForm';

export interface IdentityOwner {
  onSaveTitle(title: string): Promise<void>;
  /** Opens the 240 account (Clerk), where the name and photo are changed. Absent without Clerk. */
  onOpenAccount?(): void;
}

/**
 * The left column (R1): photo or initials, name, title, then the certifications passed in as
 * children. It comes first in the document, so on a phone it stacks above the main area. The
 * name and photo come from the owner's 240 account and are read-only here; only the title is
 * edited in Profile.
 */
export function IdentityColumn({ displayName, title, photoUrl, owner, children }: {
  displayName: string;
  title: string;
  photoUrl: string | null;
  owner?: IdentityOwner;
  children?: ReactNode;
}) {
  const [editing, setEditing] = useState(false);
  return <aside className="identity-column" aria-label={`About ${displayName}`}>
    {photoUrl
      ? <img className="avatar" src={photoUrl} alt={displayName} width={160} height={160} referrerPolicy="no-referrer" />
      : <div className="avatar avatar-initials" role="img" aria-label={displayName}>{initials(displayName)}</div>}
    {owner?.onOpenAccount && <button type="button" className="text-button account-button" onClick={owner.onOpenAccount}>Change name or photo in your 240 account</button>}
    <h1 className="profile-name">{displayName}</h1>
    {editing && owner
      ? <EntryForm
        title="Edit title" submitLabel="Save"
        fields={[{ name: 'title', label: 'Title', kind: 'text', max: MAX_TEXT_LENGTH }]}
        initial={{ title }}
        validate={(v) => {
          const checked = validateTitle(title, { title: String(v.title) });
          return checked.ok ? null : checked;
        }}
        onCancel={() => setEditing(false)}
        onSubmit={async (v) => { await owner.onSaveTitle(String(v.title).trim()); setEditing(false); }}
      />
      : <>
        {title && <p className="profile-title">{title}</p>}
        {owner && <button type="button" className="text-button" onClick={() => setEditing(true)}>{title ? 'Edit title' : 'Add your title'}</button>}
      </>}
    {children}
  </aside>;
}
